import AmbientGlow from '../components/AmbientGlow';
/**
 * DiscoverPage - Browse and search artists/tracks
 * Features: Search, genre filter, artist grid
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Search, Music, Users, Play, X, Filter, TrendingUp, Trophy, Flame, UserPlus, UserCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Genre configurations
const GENRES = [
  { id: 'all', name: 'All Genres', color: '#00F0FF' },
  { id: 'hip-hop', name: 'Hip-Hop', color: '#FF007F' },
  { id: 'rnb-soul', name: 'R&B', color: '#9333EA' },
  { id: 'country', name: 'Country', color: '#F59E0B' },
  { id: 'afrobeats', name: 'Afrobeats', color: '#39FF14' },
  { id: 'pop', name: 'Pop', color: '#EC4899' },
  { id: 'rock', name: 'Rock', color: '#EF4444' },
  { id: 'electronic', name: 'Electronic', color: '#3B82F6' },
];

// Artist Card Component
const ArtistCard = ({ artist, onClick, onFollow, canFollow }) => (
  <motion.div
    whileHover={{ scale: 1.02 }}
    whileTap={{ scale: 0.98 }}
    onClick={onClick}
    className="bg-[#12101A] rounded-2xl overflow-hidden border border-white/5 cursor-pointer hover:border-white/10 transition-colors"
  >
    <div className="aspect-square relative">
      {artist.profile_picture ? (
        <img 
          src={artist.profile_picture_thumb || artist.profile_picture} 
          alt={artist.stage_name}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 flex items-center justify-center">
          <span className="text-4xl font-bold text-white/50">
            {artist.stage_name?.[0]?.toUpperCase()}
          </span>
        </div>
      )}
      {artist.is_live && (
        <div className="absolute top-2 right-2 px-2 py-1 bg-[#FF007F] rounded-full">
          <span className="text-white text-xs font-medium">LIVE</span>
        </div>
      )}
    </div>
    <div className="p-4">
      <h3 className="text-white font-semibold truncate">{artist.stage_name}</h3>
      <p className="text-[#71717A] text-sm truncate">{artist.genres?.join(', ') || 'Artist'}</p>
      <div className="flex items-center gap-3 mt-2 text-[#52525B] text-xs">
        <span className="flex items-center gap-1">
          <Users className="w-3 h-3" />
          {artist.follower_count?.toLocaleString() || 0}
        </span>
        <span className="flex items-center gap-1">
          <Music className="w-3 h-3" />
          {artist.track_count || 0}
        </span>
      </div>
      {canFollow && (
        <button
          onClick={(e) => onFollow(e, artist.user_id, artist.is_following)}
          className={`mt-3 w-full py-2 rounded-full text-xs font-semibold transition-colors flex items-center justify-center gap-1.5 ${
            artist.is_following ? 'bg-white/10 text-white' : 'bg-[#FF007F] text-white'
          }`}
          data-testid={`discover-follow-artist-${artist.user_id}`}
        >
          {artist.is_following ? <><UserCheck className="w-3.5 h-3.5" /> Following</> : <><UserPlus className="w-3.5 h-3.5" /> Follow</>}
        </button>
      )}
    </div>
  </motion.div>
);

// Track Row Component
const TrackRow = ({ track, onPlay, isPlaying }) => (
  <motion.div
    whileHover={{ backgroundColor: 'rgba(255,255,255,0.05)' }}
    className="flex items-center gap-3 p-3 rounded-xl cursor-pointer"
    onClick={() => onPlay(track)}
  >
    <div className="w-12 h-12 rounded-lg bg-white/10 overflow-hidden flex-shrink-0 relative">
      {track.artwork ? (
        <img src={track.artwork} alt={track.track_title} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30">
          <Music className="w-5 h-5 text-[#71717A]" />
        </div>
      )}
      {isPlaying && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-[#00F0FF] border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium truncate">{track.track_title}</p>
      <p className="text-[#71717A] text-sm truncate">{track.artist_name}</p>
    </div>
    <div className="flex items-center gap-2 text-[#71717A] text-sm">
      <Play className="w-4 h-4" />
      {track.play_count?.toLocaleString() || 0}
    </div>
  </motion.div>
);

const DiscoverPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { currentTrack, playTrack, isPlaying } = useAudioPlayer();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [artists, setArtists] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('artists');
  const [network, setNetwork] = useState([]);
  const [networkLoaded, setNetworkLoaded] = useState(false);
  const [members, setMembers] = useState([]);
  const [showFollowingOnly, setShowFollowingOnly] = useState(false);
  const [topArtists, setTopArtists] = useState([]);
  const [rotation, setRotation] = useState([]);

  // Fetch Top 5 Artists + In-Rotation tracks (vote-driven, public)
  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const [taRes, rotRes] = await Promise.all([
          axios.get(`${API_URL}/api/artists/radio/top-artists?limit=5`),
          axios.get(`${API_URL}/api/artists/radio/top-voted?limit=10`),
        ]);
        setTopArtists(taRes.data?.artists || []);
        setRotation((rotRes.data?.tracks || []).filter((t) => (t.net_score || 0) > 0));
      } catch (e) {
        console.error('Failed to fetch charts:', e);
      }
    };
    fetchCharts();
  }, []);

  // Fetch the user's Radio network (their existing NLT friends + follows)
  useEffect(() => {
    const fetchNetwork = async () => {
      const token = localStorage.getItem('token');
      if (!token) { setNetworkLoaded(true); return; }
      try {
        const res = await axios.get(`${API_URL}/api/radio/network`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNetwork(res.data?.network || []);
      } catch (e) {
        console.error('Failed to fetch network:', e);
      } finally {
        setNetworkLoaded(true);
      }
    };
    fetchNetwork();
  }, []);

  // Fetch artists and tracks
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const params = new URLSearchParams();
      if (selectedGenre !== 'all') params.append('genre', selectedGenre);
      if (searchQuery) params.append('search', searchQuery);
      params.append('limit', '200');
      
      // Fetch artists (all of them)
      const artistsRes = await axios.get(
        `${API_URL}/api/artists/discover/artists?${params}`,
        { headers }
      );
      setArtists(artistsRes.data?.artists || []);
      
      // Fetch tracks
      const tracksRes = await axios.get(
        `${API_URL}/api/artists/radio/featured?${params}`,
        { headers }
      );
      setTracks(tracksRes.data?.tracks || []);

      // Fetch members directory (search-aware, genre-agnostic)
      const memberParams = new URLSearchParams();
      if (searchQuery) memberParams.append('search', searchQuery);
      memberParams.append('limit', '200');
      const membersRes = await axios.get(
        `${API_URL}/api/artists/discover/members?${memberParams}`,
        { headers }
      );
      setMembers(membersRes.data?.members || []);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedGenre, searchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchData();
    }, 300);
    
    return () => clearTimeout(debounce);
  }, [fetchData]);

  const handlePlayTrack = (track) => {
    if (playTrack) {
      playTrack(track);
    }
  };

  const sortFollowed = (arr) => [...arr].sort((a, b) => (b.is_following ? 1 : 0) - (a.is_following ? 1 : 0));
  const displayedArtists = showFollowingOnly ? artists.filter((a) => a.is_following) : sortFollowed(artists);
  const displayedMembers = showFollowingOnly ? members.filter((m) => m.is_following) : sortFollowed(members);

  const toggleFollow = async (e, id, currentlyFollowing, kind = 'artist') => {
    e.stopPropagation();
    const token = localStorage.getItem('token');
    if (!token) { navigate('/auth'); return; }
    const setList = kind === 'artist' ? setArtists : setMembers;
    const key = kind === 'artist' ? 'user_id' : 'id';
    setList((prev) => prev.map((x) => x[key] === id ? { ...x, is_following: !currentlyFollowing } : x));
    try {
      const headers = { Authorization: `Bearer ${token}` };
      if (currentlyFollowing) {
        await axios.delete(`${API_URL}/api/artists/follow/${id}`, { headers });
      } else {
        await axios.post(`${API_URL}/api/artists/follow/${id}`, {}, { headers });
      }
    } catch (err) {
      // revert on failure
      setList((prev) => prev.map((x) => x[key] === id ? { ...x, is_following: currentlyFollowing } : x));
    }
  };

  return (
    <div className="min-h-screen bg-[#050507] pb-24">
      <AmbientGlow />
      {/* Header */}
      <div className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-xl border-b border-white/5">
        <div className="px-4 py-4">
          <h1 className="text-3xl font-black text-white mb-4 tracking-tight" style={{ fontFamily: 'Unbounded' }}>
            Discover
          </h1>
          
          {/* Search Bar */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search artists, tracks..."
              className="w-full pl-12 pr-10 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#52525B] focus:outline-none focus:border-[#00F0FF]"
              data-testid="search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-white/10"
              >
                <X className="w-4 h-4 text-[#71717A]" />
              </button>
            )}
          </div>
          
          {/* Genre Filter */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {GENRES.map((genre) => (
              <button
                key={genre.id}
                onClick={() => setSelectedGenre(genre.id)}
                className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all ${
                  selectedGenre === genre.id
                    ? 'text-white'
                    : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                }`}
                style={{
                  backgroundColor: selectedGenre === genre.id ? genre.color : undefined
                }}
                data-testid={`genre-filter-${genre.id}`}
              >
                {genre.name}
              </button>
            ))}
          </div>

          {/* Following filter (Artists & Members) */}
          {isAuthenticated && (activeTab === 'artists' || activeTab === 'members') && (
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => setShowFollowingOnly((v) => !v)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors ${
                  showFollowingOnly ? 'bg-[#39FF14] text-[#050505]' : 'bg-white/5 text-[#A1A1AA] hover:bg-white/10'
                }`}
                data-testid="following-filter-toggle"
              >
                <UserCheck className="w-3.5 h-3.5" />
                {showFollowingOnly ? 'Showing Following' : 'Following only'}
              </button>
              {!showFollowingOnly && (
                <span className="text-[#52525B] text-[11px]">People you follow appear first</span>
              )}
            </div>
          )}
        </div>
        
        {/* Tabs */}
        <div className="flex border-b border-white/5">
          <button
            onClick={() => setActiveTab('artists')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'artists' 
                ? 'text-white border-b-2 border-[#FF007F]' 
                : 'text-[#71717A]'
            }`}
          >
            Artists
          </button>
          <button
            onClick={() => setActiveTab('tracks')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'tracks' 
                ? 'text-white border-b-2 border-[#00F0FF]' 
                : 'text-[#71717A]'
            }`}
          >
            Tracks
          </button>
          <button
            onClick={() => setActiveTab('members')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'members' 
                ? 'text-white border-b-2 border-[#9333EA]' 
                : 'text-[#71717A]'
            }`}
            data-testid="members-tab"
          >
            Members
          </button>
          <button
            onClick={() => setActiveTab('network')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'network' 
                ? 'text-white border-b-2 border-[#39FF14]' 
                : 'text-[#71717A]'
            }`}
            data-testid="network-tab"
          >
            My Network
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 border-2 border-[#FF007F] border-t-transparent rounded-full"
            />
          </div>
        ) : activeTab === 'artists' ? (
          <>
            {displayedArtists.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {displayedArtists.map((artist) => (
                  <ArtistCard
                    key={artist.id || artist.user_id}
                    artist={artist}
                    onClick={() => navigate(`/profile/${artist.user_id}`)}
                    onFollow={(e, id, following) => toggleFollow(e, id, following, 'artist')}
                    canFollow={isAuthenticated && artist.user_id !== user?.id}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Users className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
                <h3 className="text-xl font-semibold text-white mb-2">No Artists Found</h3>
                <p className="text-[#71717A]">
                  {showFollowingOnly
                    ? "You're not following any artists yet"
                    : searchQuery 
                    ? `No artists matching "${searchQuery}"` 
                    : 'No artists in this genre yet'}
                </p>
              </div>
            )}
          </>
        ) : activeTab === 'tracks' ? (
          <>
            {tracks.length > 0 ? (
              <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
                {tracks.map((track) => (
                  <TrackRow
                    key={track.id}
                    track={track}
                    onPlay={handlePlayTrack}
                    isPlaying={currentTrack?.id === track.id && isPlaying}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <Music className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
                <h3 className="text-xl font-semibold text-white mb-2">No Tracks Found</h3>
                <p className="text-[#71717A]">
                  {searchQuery 
                    ? `No tracks matching "${searchQuery}"` 
                    : 'No tracks in this genre yet'}
                </p>
              </div>
            )}
          </>
        ) : activeTab === 'members' ? (
          <>
            {displayedMembers.length > 0 ? (
              <>
                <p className="text-[#71717A] text-sm mb-4">
                  {showFollowingOnly
                    ? `${displayedMembers.length} member${displayedMembers.length === 1 ? '' : 's'} you follow`
                    : `${members.length} member${members.length === 1 ? '' : 's'} on NLT Radio — tap to view a profile and connect.`}
                </p>
                <div className="space-y-2" data-testid="members-list">
                  {displayedMembers.map((m) => (
                    <motion.div
                      key={m.id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => navigate(`/profile/${m.id}`)}
                      className="flex items-center gap-3 p-3 bg-[#12101A] rounded-xl border border-white/5 cursor-pointer hover:border-white/10 transition-colors"
                      data-testid={`member-item-${m.id}`}
                    >
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#9333EA]/40 to-[#00F0FF]/30 flex items-center justify-center">
                        {m.avatar_url ? (
                          <img src={m.avatar_url} alt={m.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-white/70">
                            {(m.name || m.username || '?')[0]?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{m.name || m.username}</p>
                        <p className="text-[#71717A] text-xs truncate">
                          @{m.username}{(m.city || m.state) ? ` · ${[m.city, m.state].filter(Boolean).join(', ')}` : ''}
                        </p>
                        <p className="text-[#52525B] text-[11px] flex items-center gap-1 mt-0.5">
                          <Users className="w-3 h-3" />{(m.follower_count || 0).toLocaleString()} follower{m.follower_count === 1 ? '' : 's'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          m.role === 'artist' ? 'bg-[#FF007F]/20 text-[#FF007F]'
                          : m.role === 'super_admin' ? 'bg-[#F59E0B]/20 text-[#F59E0B]'
                          : 'bg-white/10 text-[#A1A1AA]'
                        }`}>
                          {m.role === 'artist' ? 'Artist' : m.role === 'super_admin' ? 'Owner' : 'Member'}
                        </span>
                        {isAuthenticated && !m.is_self && (
                          <button
                            onClick={(e) => toggleFollow(e, m.id, m.is_following, 'member')}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${
                              m.is_following ? 'bg-white/10 text-white' : 'bg-[#9333EA] text-white'
                            }`}
                            data-testid={`discover-follow-member-${m.id}`}
                          >
                            {m.is_following ? <UserCheck className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Users className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
                <h3 className="text-xl font-semibold text-white mb-2">No Members Found</h3>
                <p className="text-[#71717A]">
                  {showFollowingOnly ? "You're not following any members yet" : searchQuery ? `No members matching "${searchQuery}"` : 'No members yet'}
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            {!networkLoaded ? (
              <div className="flex items-center justify-center py-20">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="w-8 h-8 border-2 border-[#39FF14] border-t-transparent rounded-full"
                />
              </div>
            ) : network.length > 0 ? (
              <>
                <p className="text-[#71717A] text-sm mb-4">
                  People you know from NoLiesTold — your friends and follows, now on Radio.
                </p>
                <div className="space-y-2" data-testid="network-list">
                  {network.map((n) => (
                    <motion.div
                      key={n.user_id}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                      onClick={() => navigate(n.is_artist ? `/room/${n.user_id}` : `/profile/${n.user_id}`)}
                      className="flex items-center gap-3 p-3 bg-[#12101A] rounded-xl border border-white/5 cursor-pointer hover:border-white/10 transition-colors"
                      data-testid={`network-item-${n.user_id}`}
                    >
                      <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 flex items-center justify-center">
                        {(n.artist_photo || n.profile_photo) ? (
                          <img src={n.artist_photo || n.profile_photo} alt={n.stage_name || n.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-lg font-bold text-white/60">
                            {(n.stage_name || n.name || '?')[0]?.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{n.stage_name || n.name}</p>
                        <p className="text-[#71717A] text-xs truncate">
                          {n.is_artist ? (n.latest_track ? `New: ${n.latest_track.track_title}` : (n.genres?.join(', ') || 'Artist')) : `@${n.username}`}
                        </p>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          n.relationship === 'friend_following' ? 'bg-[#39FF14]/20 text-[#39FF14]'
                          : n.relationship === 'friend' ? 'bg-[#00F0FF]/20 text-[#00F0FF]'
                          : 'bg-white/10 text-[#A1A1AA]'
                        }`}>
                          {n.relationship === 'friend_following' ? 'Friend · Following' : n.relationship === 'friend' ? 'Friend' : 'Following'}
                        </span>
                        {n.is_artist && (
                          <span className="flex items-center gap-1 text-[#52525B] text-xs">
                            <Play className="w-3 h-3" /> Room
                          </span>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-20">
                <Users className="w-16 h-16 mx-auto mb-4 text-[#52525B]" />
                <h3 className="text-xl font-semibold text-white mb-2">Your network is empty</h3>
                <p className="text-[#71717A]">
                  Follow artists or add friends on NoLiesTold — they'll appear here on Radio.
                </p>
              </div>
            )}
          </>
        )}

        {/* Top 5 Artists + In Rotation (vote-driven) */}
        {!searchQuery && (
          <>
            {topArtists.length > 0 && (
              <div className="mt-8" data-testid="top-artists-section">
                <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                  <Trophy className="w-5 h-5 text-[#F59E0B]" />
                  Top 5 Artists
                </h2>
                <div className="space-y-2">
                  {topArtists.map((a) => {
                    const clickable = a.user_id && !String(a.user_id).startsWith('admin_upload');
                    return (
                    <motion.div
                      key={a.artist_id}
                      whileHover={clickable ? { scale: 1.01 } : undefined}
                      whileTap={clickable ? { scale: 0.99 } : undefined}
                      onClick={() => clickable && navigate(`/profile/${a.user_id}`)}
                      className={`flex items-center gap-3 p-3 bg-[#12101A] rounded-xl border border-white/5 transition-colors ${clickable ? 'cursor-pointer hover:border-white/10' : 'cursor-default'}`}
                      data-testid={`top-artist-${a.rank}`}
                    >
                      <span className={`w-7 text-center font-bold text-lg shrink-0 ${
                        a.rank === 1 ? 'text-[#F59E0B]' : a.rank === 2 ? 'text-[#A1A1AA]' : a.rank === 3 ? 'text-[#CD7F32]' : 'text-[#52525B]'
                      }`}>{a.rank}</span>
                      <div className="w-11 h-11 rounded-full overflow-hidden shrink-0 bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 flex items-center justify-center">
                        {a.profile_picture ? (
                          <img src={a.profile_picture_thumb || a.profile_picture} alt={a.stage_name} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-base font-bold text-white/60">{a.stage_name?.[0]?.toUpperCase()}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{a.stage_name}</p>
                        <p className="text-[#71717A] text-xs truncate">{a.track_count} track{a.track_count === 1 ? '' : 's'} · {a.total_plays?.toLocaleString() || 0} plays</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 text-[#39FF14] font-semibold text-sm">
                        <Flame className="w-4 h-4" />
                        {a.score > 0 ? '+' : ''}{a.score}
                      </div>
                    </motion.div>
                    );
                  })}
                </div>
              </div>
            )}

            {rotation.length > 0 && (
              <div className="mt-8" data-testid="rotation-section">
                <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-[#FF007F]" />
                  In Rotation
                </h2>
                <div className="bg-[#12101A] rounded-2xl border border-white/5 overflow-hidden">
                  {rotation.map((track) => (
                    <TrackRow
                      key={track.id}
                      track={track}
                      onPlay={handlePlayTrack}
                      isPlaying={currentTrack?.id === track.id && isPlaying}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default DiscoverPage;
